import { EditOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { ThreeDotMenu, Tooltip } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { QuestionCircleIcon } from '@mm/icons';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { useEmailAccountCRUDActions } from '@mm/state/actions/EmailAccountCRUDActions';
import {
  EmailAccount,
  EmailAccountKinds,
} from '@mm/state/interfaces/emailAccount';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectActiveEmailAccounts } from '@mm/state/selectors/emailAccountsSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { OAuthPopupActionButton } from '@mm/stateful-components/OAuth/OAuthPopupActionButton';
import { ReactComponent as CustomizeIcon } from 'assets/customize.svg';
import { ReactComponent as GoogleIcon } from 'assets/icons/google-colorful-icon.svg';
import { ReactComponent as MicrosoftIcon } from 'assets/icons/microsoft-colorful-icon.svg';
import { ReactComponent as SendGridIcon } from 'assets/sendgrid.svg';
import classNames from 'classnames';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { useActivateEmailAccount } from '../../../../../../../../common/modals/ActivateEmailAccountModal/use-activate-email-account';
import { ReactComponent as AccountIcon } from '../../../../../../../../layout/BotBuilderSidebar/UserDropdown/UserDropdownIcons/UserFallback.svg';
import { BaseIntegration } from '../../../BaseIntegration/BaseIntegration';
import { BaseIntegrationHeader } from '../../../BaseIntegration/BaseIntegrationHeader';
import { ReactComponent as AddCustomAccountIcon } from '../../../icons/add-custom-account.svg';
import { ReactComponent as AddGmailAccountIcon } from '../../../icons/add-gmail-account.svg';
import { ReactComponent as AddMicrosoftlAccountIcon } from '../../../icons/add-microsoft-account.svg';
import { ReactComponent as AddSendGridAccountIcon } from '../../../icons/add-sendgrid-account.svg';
import { IntegrationSectionProps } from '../../../IntegrationsPanel/IntegrationsPanel';
import { EmailDeliverabilityScore } from '../EmailDeliverabilityScore/EmailDeliverabilityScore';
import { renderMessageFrequency } from './EmailAccountEditor';
import { getEmailAccountsWithErrors } from '@mm/state/helpers/connection-helpers';
import { SendingDomainSetup } from '../SendingDomainSetup/SendingDomainSetup';

const isGmail = (account?: EmailAccount) =>
  account?.emailAddress?.includes('gmail.com') && account.kind === 'gmail';

export const EmailIntegration: FunctionComponent<IntegrationSectionProps> = ({
  section,
}) => {
  const { getAccounts, disconnect, getGmailOauthUrl, getMicrosoftOauthUrl } =
    useEmailAccountCRUDActions();
  const { retrievePage: retrieveFacebookPage } = useConnectedPageCRUDActions();
  const { connectSendgrid, connectManualAccount } = useActivateEmailAccount();
  const { openGeneralConfirmModal } = useModalActions();
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const accounts = useSelector(selectActiveEmailAccounts);

  const featureProps = {
    landmineName: landmineNames.INTEGRATIONS_EMAIL,
    featureFlag: 'nativeIntegrations',
  };

  const configs = [
    {
      kind: 'gmail',
      icon: <GoogleIcon />,
      emptyStateIcon: <AddGmailAccountIcon />,
      emptyStateButton: (
        <OAuthPopupActionButton
          fetchOauthPopupUrl={async () =>
            getGmailOauthUrl(facebookPageId, 'post_message')
          }
          oauthSuccessMessageType="googleAccountConnected"
          onRefreshNeeded={async () => {
            await getAccounts(facebookPageId);
          }}
          spinnerProps={{ size: 16, width: 32 }}
          buttonProps={{
            css: { container: 'integrations__empty-state-button' },
          }}
          {...featureProps}
        >
          Add a Gmail Account
        </OAuthPopupActionButton>
      ),
      title: 'Gmail',
      subtitle:
        'Connect with Gmail to send high volume emails without sacrificing deliverability.',
      css: { icon: 'integrations__header-icon-bordered' },
    },
    {
      kind: 'sendgrid',
      icon: <SendGridIcon />,
      emptyStateIcon: <AddSendGridAccountIcon />,
      emptyStateButtonClickAction: () => connectSendgrid(),
      title: 'SendGrid',
      subtitle:
        'Connect with SendGrid to send high volume emails without sacrificing deliverability.',
      css: { icon: 'integrations__header-icon-bordered' },
    },
    {
      kind: 'microsoft',
      icon: <MicrosoftIcon />,
      emptyStateIcon: <AddMicrosoftlAccountIcon />,
      emptyStateButton: (
        <OAuthPopupActionButton
          fetchOauthPopupUrl={async () => getMicrosoftOauthUrl(facebookPageId)}
          oauthSuccessMessageType="microsoftAccountConnected"
          onRefreshNeeded={async () => {
            await getAccounts(facebookPageId);
          }}
          spinnerProps={{ size: 16, width: 32 }}
          buttonProps={{
            css: { container: 'integrations__empty-state-button' },
          }}
          {...featureProps}
        >
          Add a Microsoft Account
        </OAuthPopupActionButton>
      ),
      title: 'Microsoft',
      subtitle:
        'Connect with Microsoft to send high volume emails without sacrificing deliverability.',
      css: { icon: 'integrations__header-icon-bordered' },
    },
    {
      kind: 'custom',
      icon: <CustomizeIcon />,
      emptyStateIcon: <AddCustomAccountIcon />,
      emptyStateButtonClickAction: () => connectManualAccount(),
      title: 'Manually connected emails',
      subtitle:
        'Connect your email providers to send automated outreach to your leads.',
      css: { icon: 'integrations__header-icon-manual-suppression' },
    },
  ];

  const { openActivateEmailAccountModal, openEmailAccountEditorModal } =
    useModalActions();

  const onConnectAccountClick = () => {
    openActivateEmailAccountModal();
  };

  const onDisconnect = (account: EmailAccount) => {
    openGeneralConfirmModal({
      headerText: 'Confirm Account Disconnection',
      messageText: (
        <>
          <div>
            Once disconnected, all existing conversations via this email account
            will be terminated.
          </div>

          <div>
            <strong>Disconnected accounts cannot be restored.</strong>
          </div>
        </>
      ),
      onCloseHandler: async (ok: boolean) => {
        if (ok) {
          await disconnect(account);
          retrieveFacebookPage(facebookPageId);
        }
      },
      okButtonText: 'Disconnect',
      cancelButtonFirst: true,
      okButtonStyling: {
        red: true,
      },
      css: {
        primaryButton: 'integrations__diconnect-modal-primary-button',
        secondaryButton: 'integrations__confirm-modal-secondary-button',
      },
    });
  };
  useEffect(() => {
    getAccounts(facebookPageId);
  }, [facebookPageId, getAccounts]);

  const elements = [{ name: 'Disconnect', value: 'disconnect' }];

  const onMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const header = kind => {
    const accountsByKind = accounts.filter(acc => acc.kind === kind);

    return (
      <div
        className={classNames({
          'integrations__header--account-connected': accounts.length,
        })}
      >
        <div className="integrations__header-actions">
          {!!accountsByKind.length && (
            <div className="integrations__header-status">
              <AccountIcon className="integrations__header-status-icon" />
              <div className="integrations__header-status-count">
                {accountsByKind.length}
              </div>
              Accounts
            </div>
          )}

          {kind === 'microsoft' || kind === 'gmail' ? (
            <OAuthPopupActionButton
              {...featureProps}
              fetchOauthPopupUrl={async () => {
                switch (kind) {
                  case 'microsoft':
                    return getMicrosoftOauthUrl(facebookPageId);
                  case 'gmail':
                    return getGmailOauthUrl(facebookPageId, 'post_message');
                  default:
                    return undefined;
                }
              }}
              oauthSuccessMessageType={
                kind === 'microsoft'
                  ? 'microsoftAccountConnected'
                  : 'googleAccountConnected'
              }
              onRefreshNeeded={async () => {
                await getAccounts(facebookPageId);
              }}
              spinnerProps={{ size: 16, width: 32 }}
              buttonProps={{
                color: ButtonColor.secondaryBlue,
                css: {
                  container: classNames(
                    'integrations__header-add-button-secondary',
                    'integrations__header-button-no-margin',
                  ),
                },
              }}
            >
              Add an Account
            </OAuthPopupActionButton>
          ) : (
            <PrimaryButton
              className={classNames(
                'integrations__header-add-button-secondary',
                'integrations__header-button-no-margin',
              )}
              onClick={e => {
                e?.stopPropagation();
                switch (kind as EmailAccountKinds) {
                  case 'custom':
                    connectManualAccount();
                    break;
                  case 'sendgrid':
                    connectSendgrid();
                    break;
                }
              }}
              color={ButtonColor.secondaryBlue}
            >
              Add an Account
            </PrimaryButton>
          )}
        </div>
      </div>
    );
  };

  const modalActions = useModalActions();
  const onIncreaseLimitClick = () => {
    modalActions.openIncreaseEmailLimitModal({
      onSubmit: onConnectAccountClick,
    });
  };

  const renderLimited = (account?: EmailAccount) => {
    if (isGmail(account)) {
      return (
        <div
          className="integrations-item__limit-notice"
          onClick={onIncreaseLimitClick}
        >
          <span>Sending is limited</span>
          <span>Connect more</span>
        </div>
      );
    }

    return <></>;
  };

  const [accountsWithAuthErrors, setAccountsWithAuthErrors] = useState(
    [] as string[],
  );
  const [sectionsWithErrors, setSectionsWithErrors] = useState([] as string[]);

  const fetchAccountErrors = accounts => {
    const accountsWithErrors = getEmailAccountsWithErrors(accounts);

    setSectionsWithErrors(
      accountsWithErrors
        .map(acc => acc.kind)
        .filter((acc, index, self) => self.indexOf(acc) === index),
    );
    setAccountsWithAuthErrors(accountsWithErrors.map(acc => acc.emailAddress));
  };

  useEffect(() => {
    fetchAccountErrors(accounts);
  }, [accounts]);

  return (
    <div className="native-integrations">
      <SendingDomainSetup />
      <EmailDeliverabilityScore compactView />

      {configs.map(config => {
        const accountsByKind = accounts.filter(acc => acc.kind === config.kind);
        return (
          <BaseIntegration
            key={config.title}
            titleComponent={
              <BaseIntegrationHeader
                title={config.title}
                subtitle={config.subtitle}
                icon={config.icon}
                headerRightComponent={header(config.kind)}
                css={config?.css}
                haveErrors={
                  sectionsWithErrors.length > 0 &&
                  sectionsWithErrors.includes(config.kind)
                }
                {...featureProps}
              />
            }
            integrationType={config.kind}
            showEmptyState={!accountsByKind.length}
            emptyStateConfig={{
              button: config.emptyStateButton
                ? config.emptyStateButton
                : `Add ${
                    config.kind === 'custom' ? 'an Email' : `a ${config.title}`
                  } Account`,
              onClick: config.emptyStateButtonClickAction,
              icon: config.emptyStateIcon,
              ...featureProps,
            }}
          >
            {!!accountsByKind?.length && (
              <div className="integrations-item__your-accounts">
                <table className="integrations-item__table">
                  <thead>
                    <th>Sender Name</th>
                    <th>Email Address</th>
                    <th>Emails queued</th>
                    <th>Email frequency</th>
                    <th>Sent 24h</th>
                    <th>Actions</th>
                  </thead>
                  <tbody>
                    {accountsByKind.map(account => (
                      <tr key={account.id}>
                        <td>{account.name}</td>
                        <td>
                          <div className="integrations-item__email-address">
                            {account.emailAddress} {renderLimited(account)}
                            {account.kind === 'gmail' &&
                              account.credential &&
                              !account.credential.fullGmailScope && (
                                <>
                                  <OAuthPopupActionButton
                                    {...featureProps}
                                    fetchOauthPopupUrl={async () => {
                                      if (
                                        account.kind === 'gmail' &&
                                        account.credential?.email
                                      ) {
                                        return `${await getGmailOauthUrl(
                                          facebookPageId,
                                          'post_message',
                                        )}&login_hint=${
                                          account.credential.email
                                        }`;
                                      }
                                    }}
                                    oauthSuccessMessageType="googleAccountConnected"
                                    buttonProps={{ color: 'blue', size: 'sm' }}
                                    spinnerProps={{ size: 16, width: 32 }}
                                    onRefreshNeeded={() => {
                                      getAccounts(facebookPageId);
                                    }}
                                  >
                                    Reauthorize Account
                                  </OAuthPopupActionButton>
                                  <Tooltip
                                    title="  Reauthorizing your account will allow us to
                                    send email using a more secure method,
                                    ensuring more reliable message delivery"
                                  >
                                    <QuestionCircleIcon />
                                  </Tooltip>
                                </>
                              )}
                            {(account.kind === 'sendgrid' &&
                              !account.credential?.accessTokenMasked && (
                                <Tooltip
                                  title="  To continue sending messages, please update
                                  the SendGrid API Key"
                                >
                                  <ExclamationCircleOutlined
                                    style={{ color: '#ff4d4f' }}
                                  />
                                </Tooltip>
                              )) ||
                              (accountsWithAuthErrors.includes(
                                account.emailAddress,
                              ) && (
                                <Tooltip
                                  title=" There is an error with your connection. Please
                                  check your account permissions or reconnect"
                                >
                                  <ExclamationCircleOutlined
                                    style={{ color: '#ff4d4f' }}
                                  />
                                </Tooltip>
                              ))}
                          </div>
                        </td>

                        <td>{account.queue?.inQueueCount || 0}</td>

                        <td>
                          {renderMessageFrequency(
                            account.messagesFrequency || 0,
                          )}
                        </td>

                        <td>{account.queue?.sentLastDayCount || 0}</td>

                        <td className="integrations__three-dot-menu-wrapper">
                          <EditOutlined
                            className="integrations__edit-icon"
                            onClick={() =>
                              openEmailAccountEditorModal({
                                emailAccountUnderEdit: account,
                              })
                            }
                          />
                          <ThreeDotMenu
                            className="integrations__three-dot-menu"
                            elements={elements}
                            selectItem={value =>
                              value === 'disconnect' && onDisconnect(account)
                            }
                            onClick={onMenuClick}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </BaseIntegration>
        );
      })}
    </div>
  );
};
